import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ArrowRight } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, Legend, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { apiBaseUrl, authHeaders } from "./shared";
import MilesReconciliation from "./MilesReconciliation";

// Same fixed hue-per-category convention the Fleet Command Center home page
// uses for its own cost-breakdown donut -- keeping the mapping identical
// means "orange" reads as "fuel" everywhere in the app, not just here.
const COST_COLOR = {
  fuel_cost: "#f2994a", toll_cost: "#2563eb", driver_cost: "#1d6fd8",
  insurance_cost: "#e0745d", office_payroll_cost: "#c9a227", maintenance_cost: "#6b7280",
  other_cost: "#8b7fd6",
};
const COST_LABEL = {
  fuel_cost: "Fuel", toll_cost: "Tolls", driver_cost: "Driver pay",
  insurance_cost: "Insurance", office_payroll_cost: "Office/payroll", maintenance_cost: "Maintenance",
  // Each truck's share of the real fleet.equipment_price_per_week target,
  // prorated per trip -- not a leftover/misc bucket (see estimate-trip-cost/
  // match-truck-for-load/find-next-load's other_cost).
  other_cost: "Equipment",
};

function firstOfMonthIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// Shared by both /dispatch/reports and /dispatch/finance -- same underlying
// numbers (fleet_stats_summary + fuel_tax_report, the exact functions the
// Fleet Command Center's own MTD cards already call), just with a real
// date-range picker instead of a fixed month-to-date window. Finance is
// role-gated to admin/super_admin in the sidebar already; Reports is open
// to any dispatcher -- both showing the same data is intentional, not a
// placeholder duplicate, since there's no separate "finance-only" number
// this app tracks (no invoicing/AR table exists).
export default function FinanceReports() {
  const [start, setStart] = useState(firstOfMonthIso());
  const [end, setEnd] = useState(todayIso());
  const [stats, setStats] = useState(null);
  const [fuelTax, setFuelTax] = useState(null);
  const [hazmat, setHazmat] = useState(null);
  const [hazmatLoading, setHazmatLoading] = useState(true);
  const [weeklyProfit, setWeeklyProfit] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendLoading, setTrendLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Independent of the date-range picker above -- a trend line is
  // inherently "the last N calendar weeks", not an arbitrary custom range;
  // the same real revenue/profit basis (chosen_route_row/contribution_profit)
  // every other number on this page already uses, just plotted over time
  // instead of summed into one total. This is the one thing nowhere else in
  // the app shows: everything else here is a current-range snapshot.
  useEffect(() => {
    setTrendLoading(true);
    axios.get(`${apiBaseUrl}/scripted/fleet/revenue-profit-trend`, { ...authHeaders(), params: { weeks: 12 } })
      .then((res) => setTrend(res.data))
      .catch(() => setTrend(null))
      .finally(() => setTrendLoading(false));
  }, []);

  // Independent of the date-range picker above (and its own failure
  // handling) -- "is a truck covering its equipment cost" is inherently a
  // THIS CALENDAR WEEK question (same fixed framing Fleet Dashboard's
  // identical widget uses), not something that means anything over an
  // arbitrary custom range, so it doesn't take start/end params.
  useEffect(() => {
    axios.get(`${apiBaseUrl}/scripted/fleet/weekly-truck-profit`, authHeaders())
      .then((res) => setWeeklyProfit(res.data))
      .catch(() => setWeeklyProfit(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const now = new Date(end);
    Promise.all([
      axios.get(`${apiBaseUrl}/scripted/fleet/stats-summary`, {
        ...authHeaders(),
        params: { start: `${start}T00:00:00`, end: `${end}T23:59:59` },
      }),
      axios.get(`${apiBaseUrl}/scripted/fleet/fuel-tax-report`, {
        ...authHeaders(),
        params: { year: now.getFullYear(), quarter: Math.floor(now.getMonth() / 3) + 1 },
      }),
    ])
      .then(([statsRes, fuelTaxRes]) => {
        setStats(statsRes.data);
        setFuelTax(fuelTaxRes.data);
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [start, end]);

  // Same [start, end] range and same real in_transit/delivered population
  // as every other card on this page (both go through fleet_stats.py's
  // shared _loads_in_range) -- kept as its own request/loading state
  // rather than folded into the Promise.all above so one endpoint being
  // slow/erroring never blocks the other.
  useEffect(() => {
    setHazmatLoading(true);
    axios.get(`${apiBaseUrl}/scripted/fleet/hazmat-report`, {
      ...authHeaders(),
      params: { start: `${start}T00:00:00`, end: `${end}T23:59:59` },
    })
      .then((res) => setHazmat(res.data))
      .catch(() => setHazmat(null))
      .finally(() => setHazmatLoading(false));
  }, [start, end]);

  const money = stats?.money;
  const utilization = stats?.utilization;
  const costBreakdown = stats?.cost_breakdown;
  const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);

  const costChartData = useMemo(() => {
    if (!costBreakdown) return [];
    return Object.keys(COST_LABEL)
      .map((key) => ({ key, name: COST_LABEL[key], value: Number(costBreakdown[key]) || 0 }))
      .filter((d) => d.value > 0);
  }, [costBreakdown]);
  const costTotal = costChartData.reduce((sum, d) => sum + d.value, 0);

  const fuelTaxChartData = useMemo(() => {
    if (!fuelTax?.fuel_tax_by_state) return [];
    return Object.entries(fuelTax.fuel_tax_by_state)
      .map(([state, amount]) => ({ state, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [fuelTax]);

  // Same computation Fleet Dashboard's identical widget uses -- kept
  // consistent so the two pages never disagree about who's covering their
  // equipment cost this week.
  const coverage = useMemo(() => {
    if (!weeklyProfit?.trucks?.length) return null;
    const list = weeklyProfit.trucks;
    const covered = list.filter((t) => t.covered).length;
    const worst = [...list].sort((a, b) => a.balance - b.balance).filter((t) => !t.covered).slice(0, 3);
    return { covered, total: list.length, worst };
  }, [weeklyProfit]);

  const trendChartData = useMemo(() => {
    if (!trend?.weeks?.length) return [];
    return trend.weeks.map((w) => ({
      week: new Date(w.week_start).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }),
      revenue: w.revenue,
      profit: w.profit,
    }));
  }, [trend]);

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Reports</h1>
          <div style={styles.subtitle}>Profit, cost, and utilization for a date range you choose.</div>
        </div>
        <div style={styles.dateRange}>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={styles.dateInput} max={end} />
          <span style={styles.dateSep}>to</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={styles.dateInput} min={start} max={todayIso()} />
        </div>
      </div>

      {error && <div style={styles.errorBox}>Couldn't load report data: {error}</div>}

      <div style={styles.sectionLabel}>Money</div>
      <div style={styles.statGrid}>
        <StatCard label="Revenue" value={loading ? "…" : fmt(money?.revenue)} />
        <StatCard label="Profit" value={loading ? "…" : fmt(money?.profit)} accent />
        <StatCard label="Profit / mile" value={loading ? "…" : money?.profit_per_mile != null ? `$${money.profit_per_mile.toFixed(2)}` : "—"} />
        <StatCard label="Loads" value={loading ? "…" : money?.loads_count ?? "—"} />
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>Hazmat Split</div>
        {hazmatLoading && <div style={styles.emptyNote}>Loading…</div>}
        {!hazmatLoading && (!hazmat || hazmat.total_loads === 0) && (
          <div style={styles.emptyNote}>No assigned or closed loads in this range.</div>
        )}
        {!hazmatLoading && hazmat && hazmat.total_loads > 0 && (
          <>
            <div style={styles.hazmatBar}>
              {hazmat.hazardous_pct != null && (
                <div style={{ ...styles.hazmatBarSeg, width: `${hazmat.hazardous_pct}%`, background: "#dc2626" }} />
              )}
              {hazmat.non_hazardous_pct != null && (
                <div style={{ ...styles.hazmatBarSeg, width: `${hazmat.non_hazardous_pct}%`, background: "#16a34a" }} />
              )}
            </div>
            <div style={styles.hazmatLegend}>
              <div style={styles.hazmatLegendRow}>
                <span style={{ ...styles.legendDot, background: "#dc2626" }} />
                <span style={styles.costLegendName}>Hazardous</span>
                <span style={styles.costLegendValue}>{hazmat.hazardous_pct != null ? `${hazmat.hazardous_pct}%` : "—"}</span>
                <span style={styles.costLegendPct}>{hazmat.hazardous_count}</span>
              </div>
              <div style={styles.hazmatLegendRow}>
                <span style={{ ...styles.legendDot, background: "#16a34a" }} />
                <span style={styles.costLegendName}>Non-Hazardous</span>
                <span style={styles.costLegendValue}>{hazmat.non_hazardous_pct != null ? `${hazmat.non_hazardous_pct}%` : "—"}</span>
                <span style={styles.costLegendPct}>{hazmat.non_hazardous_count}</span>
              </div>
            </div>
            {hazmat.unclassified_count > 0 && (
              <div style={styles.caption}>
                {hazmat.unclassified_count} load{hazmat.unclassified_count === 1 ? "" : "s"} in this range {hazmat.unclassified_count === 1 ? "has" : "have"} no
                hazmat classification (booked before this field existed) -- excluded from the percentages above, counted in neither bucket.
              </div>
            )}
          </>
        )}
      </div>

      <div style={styles.sectionLabel}>Trend, last 12 weeks</div>
      <div style={styles.card}>
        <div style={styles.cardTitle}>Revenue &amp; profit by week</div>
        {trendLoading && <div style={styles.emptyNote}>Loading…</div>}
        {!trendLoading && trendChartData.every((w) => w.revenue === 0 && w.profit === 0) && (
          <div style={styles.emptyNote}>No loads with a real cost record in the last 12 weeks.</div>
        )}
        {!trendLoading && !trendChartData.every((w) => w.revenue === 0 && w.profit === 0) && (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trendChartData} margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
              <RTooltip formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#1d6fd8" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="profit" name="Profit" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div style={styles.grid2}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Cost Breakdown</div>
          {costChartData.length === 0 && <div style={styles.emptyNote}>{loading ? "Loading…" : "No cost data in this range."}</div>}
          {costChartData.length > 0 && (
            <div style={styles.costChartRow}>
              <div style={styles.donutWrap}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={costChartData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={58} paddingAngle={2} strokeWidth={0}>
                      {costChartData.map((d) => <Cell key={d.key} fill={COST_COLOR[d.key]} />)}
                    </Pie>
                    <RTooltip
                      formatter={(v) => `$${Number(v).toLocaleString("en-US")}`}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--line)" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div style={styles.donutCenter}>
                  <div style={styles.donutCenterValue}>{fmt(costTotal)}</div>
                  <div style={styles.donutCenterLabel}>total cost</div>
                </div>
              </div>
              <div style={styles.costLegend}>
                {costChartData.map((d) => (
                  <div key={d.key} style={styles.costLegendRow}>
                    <span style={{ ...styles.legendDot, background: COST_COLOR[d.key] }} />
                    <span style={styles.costLegendName}>{d.name}</span>
                    <span style={styles.costLegendValue}>{fmt(d.value)}</span>
                    <span style={styles.costLegendPct}>{costTotal ? Math.round((d.value / costTotal) * 100) : 0}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <MilesReconciliation start={start} end={end} />
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>
          Weekly Equipment Cost Coverage
          <Link to="/dispatch/truck-economics" style={styles.cardTitleLink}>Full breakdown <ArrowRight size={11} /></Link>
        </div>
        {!weeklyProfit && <div style={styles.emptyNote}>Loading…</div>}
        {coverage && (
          <>
            <div style={styles.coverageHeadline}>
              <ShieldCheck size={18} color="#16a34a" />
              <span style={styles.coverageHeadlineValue}>{coverage.covered} / {coverage.total}</span>
              <span style={styles.coverageHeadlineLabel}>
                trucks are covering their ${weeklyProfit.equipment_price_per_week.toLocaleString("en-US")}/wk equipment cost
              </span>
            </div>
            <div style={styles.coverageBar}>
              <div style={{ ...styles.coverageBarFill, width: coverage.total ? `${(coverage.covered / coverage.total) * 100}%` : "0%" }} />
            </div>
            {coverage.worst.length > 0 ? (
              <div style={styles.worstList}>
                <div style={styles.worstListLabel}>Furthest behind this week</div>
                {coverage.worst.map((t) => (
                  <div key={t.truck_id} style={styles.worstRow}>
                    <ShieldAlert size={13} color="#dc2626" style={{ flexShrink: 0 }} />
                    <span style={styles.worstUnit}>{t.unit_number}</span>
                    <span style={styles.worstBalance}>-{fmt(Math.abs(t.balance))}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={styles.allCoveredNote}>Every truck is covering its cost this week. ✓</div>
            )}
            <div style={styles.caption}>
              This is the same real equipment cost already baked into every load's profit above (see the Equipment
              slice in Cost Breakdown) -- this view just shows whether each truck's week-to-date profit has actually
              cleared it yet, not a separate charge.
            </div>
          </>
        )}
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>
          Fuel Tax -- {fuelTax ? `Q${fuelTax.quarter} ${fuelTax.year}` : "current quarter"}
        </div>
        {!loading && fuelTax && fuelTax.loads_with_fuel_data === 0 && (
          <div style={styles.emptyNote}>No loads with a saved fuel-stop route plan yet this quarter.</div>
        )}
        {fuelTax && fuelTax.loads_with_fuel_data > 0 && (
          <>
            <div style={styles.fuelTaxTotals}>
              <div>
                <div style={styles.fuelTaxTotalValue}>{fmt(fuelTax.total_fuel_tax)}</div>
                <div style={styles.fuelTaxTotalLabel}>Total fuel tax paid</div>
              </div>
              <div>
                <div style={styles.fuelTaxTotalValue}>{fmt(fuelTax.total_fuel_cost)}</div>
                <div style={styles.fuelTaxTotalLabel}>Total fuel spend</div>
              </div>
              <div>
                <div style={styles.fuelTaxTotalValue}>{Math.round(fuelTax.total_gallons_purchased).toLocaleString("en-US")}</div>
                <div style={styles.fuelTaxTotalLabel}>Gallons purchased</div>
              </div>
              <div>
                <div style={styles.fuelTaxTotalValue}>{fuelTax.loads_with_fuel_data}</div>
                <div style={styles.fuelTaxTotalLabel}>Loads ({fuelTax.fuel_stop_count} fuel stops)</div>
              </div>
            </div>
            {fuelTaxChartData.length > 0 && (
              <div style={styles.barChartWrap}>
                <ResponsiveContainer width="100%" height={Math.max(140, fuelTaxChartData.length * 30)}>
                  <BarChart data={fuelTaxChartData} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                    <XAxis type="number" tickFormatter={(v) => `$${v.toLocaleString("en-US")}`} tick={{ fontSize: 11, fill: "var(--muted)" }} />
                    <YAxis type="category" dataKey="state" width={36} tick={{ fontSize: 12, fill: "var(--ink-2)" }} />
                    <RTooltip
                      formatter={(v) => `$${Number(v).toLocaleString("en-US")}`}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--line)" }}
                    />
                    <Bar dataKey="amount" fill={COST_COLOR.fuel_cost} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div style={styles.caption}>
              Expense reporting only (what was actually paid at the pump) -- not a full IFTA net reconciliation, which
              would also require per-state miles driven to compute refund/owe direction.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div style={styles.miniStatValue}>{value}</div>
      <div style={styles.miniStatLabel}>{label}</div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statValue, ...(accent ? { color: "var(--brand)" } : {}) }}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4 },
  dateRange: { display: "flex", alignItems: "center", gap: 8 },
  dateInput: {
    fontSize: 12.5, color: "var(--ink)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 10px",
  },
  dateSep: { fontSize: 12, color: "var(--muted)" },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },

  sectionLabel: { fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4, margin: "4px 0 8px" },
  statGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 },
  statCard: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: "16px 18px", boxShadow: "var(--shadow-xs)",
  },
  statValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  statLabel: { fontSize: 12, color: "var(--muted)", marginTop: 2 },

  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start", marginBottom: 20 },
  card: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: 18, boxShadow: "var(--shadow-xs)",
  },
  cardTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 14 },
  cardTitleLink: {
    marginLeft: "auto", display: "flex", alignItems: "center", gap: 3, fontSize: 11.5, fontWeight: 600,
    color: "var(--brand)", textDecoration: "none",
  },
  emptyNote: { fontSize: 12.5, color: "var(--muted)" },

  coverageHeadline: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  coverageHeadlineValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)" },
  coverageHeadlineLabel: { fontSize: 12.5, color: "var(--muted)", lineHeight: 1.4 },
  coverageBar: { height: 7, borderRadius: 999, background: "rgba(220,38,38,0.15)", overflow: "hidden", margin: "10px 0 14px" },
  coverageBarFill: { height: "100%", background: "#16a34a", borderRadius: 999 },
  worstList: { display: "flex", flexDirection: "column", gap: 6 },
  worstListLabel: { fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 2 },
  worstRow: { display: "flex", alignItems: "center", gap: 7, fontSize: 12.5 },
  worstUnit: { color: "var(--ink-2)", fontWeight: 600, flex: 1 },
  worstBalance: { color: "#dc2626", fontWeight: 700 },
  allCoveredNote: { fontSize: 12.5, color: "#16a34a", fontWeight: 600 },

  hazmatBar: { height: 10, borderRadius: 999, overflow: "hidden", display: "flex", background: "var(--line)", marginBottom: 12 },
  hazmatBarSeg: { height: "100%" },
  hazmatLegend: { display: "flex", flexDirection: "column", gap: 9 },
  hazmatLegendRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 },

  costChartRow: { display: "flex", alignItems: "center", gap: 20 },
  donutWrap: { position: "relative", width: 130, height: 130, flexShrink: 0 },
  donutCenter: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" },
  donutCenterValue: { fontSize: 14, fontWeight: 800, color: "var(--ink)" },
  donutCenterLabel: { fontSize: 9.5, color: "var(--muted)" },
  costLegend: { flex: 1, display: "flex", flexDirection: "column", gap: 9, minWidth: 0 },
  costLegendRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 },
  legendDot: { width: 9, height: 9, borderRadius: "50%", display: "inline-block", flexShrink: 0 },
  costLegendName: { color: "var(--ink-2)", flex: 1 },
  costLegendValue: { color: "var(--ink)", fontWeight: 700 },
  costLegendPct: { color: "var(--muted)", fontSize: 11, width: 32, textAlign: "right" },

  miniStatGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  miniStatValue: { fontSize: 18, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  miniStatLabel: { fontSize: 11.5, color: "var(--muted)", marginTop: 2 },

  fuelTaxTotals: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 18 },
  fuelTaxTotalValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  fuelTaxTotalLabel: { fontSize: 12, color: "var(--muted)", marginTop: 2 },
  barChartWrap: { marginTop: 4 },
  caption: { fontSize: 11, color: "var(--muted)", marginTop: 12 },
};
